/// <amd-module name="Repos/Views/Branches/Services/BranchesService" />
import { EnhancedGitRef } from "Data/EnhancedGitRef";
import {
    GitBranchStats,
    GitPushSearchCriteria,
    GitRef,
    GitRefFavorite,
    GitRepository,
    GitVersionDescriptor,
    GitVersionOptions,
    GitVersionType
} from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { EMPTY_OBJECT_ID, branchNameToRef, refToBranchName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { getReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { SparseTreeProvider } from "Repos/SparseTree/SparseTreeProvider";
import { ISparseTreeProviderFetchResult, SparseTreeNode } from "Repos/SparseTree/SparseTreeProvider.Types";
import { IObservableValue, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { issueRequest } from "VSS/Platform/Fetch";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { BranchTableProvider } from "../Provider/BranchTableProvider";
import { BranchTableProviderFetchResult } from "../Provider/BranchTableProvider.Types";
import { BranchTreeProvider } from "../Provider/BranchTreeProvider";
import { GitClientName, IGitRestClient, getGitClient } from "../RestClient/Git";
import { BranchTypeFilter, DeletedBranch, IBranchesProviderData, IBranchesQuery, IBranchesService } from "./BranchesService.Types";

function mapFromArray<T>(items: T[] | undefined, identifierFunction: (item: T) => string): { [key: string]: T };
function mapFromArray<T>(items: T[] | undefined, identifierFunction: (item: T) => number): { [key: number]: T };
function mapFromArray<T>(items: T[] | undefined, identifierFunction: (item: T) => string | number): { [key: string]: T } | { [key: number]: T } {
    const map: { [branchName: string]: T } = {};
    items && items.forEach(x => (map[identifierFunction(x)] = x));
    return map;
}

interface EnhanceResult {
    branches: EnhancedGitRef[];
    compareBranch?: EnhancedGitRef;
    defaultBranch?: EnhancedGitRef;
    isUserSetCompareBranch?: boolean;
}

interface DataProviderParams {
    projectName: string;
    repositoryName: string;
    filterString?: string;
}

interface BranchDataResult {
    branches?: GitRef[];
    fetchAsync?: boolean;
}

const BranchSeparator = "/";
const AllBranchesDataProviderContributionId = "ms.vss-code-web.all-branches-data-provider";
const MyBranchesDataProviderContributionId = "ms.vss-code-web.my-branches-data-provider";
const StaleBranchesDataProviderContributionId = "ms.vss-code-web.stale-branches-data-provider";
class GitBranchesService extends VssService implements IBranchesService {
    private mostRecentFavorites: { [key: string]: GitRefFavorite } = {};
    private selectedTabId: ObservableValue<string>;
    private filterString: string = "";
    private isTargetBranchUpdates2Enabled: boolean = false;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const pageStateHistoryService = pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const queryParams = pageStateHistoryService.getProjectedState();

        this.selectedTabId = new ObservableValue(queryParams["_a"] || "mine");
        pageStateHistoryService.registerObservable(
            this.selectedTabId,
            "_a",
            HistoryBehavior.replace,
            value => (value === "mine" ? "" : value),
            fromUrl => fromUrl || "mine"
        );

        this.isTargetBranchUpdates2Enabled = isFeatureFlagEnabled(pageContext, "WebAccess.VersionControl.PullRequests.UpdateTargetBranches2", false);
    }

    public getBranchTableProvider(branchesQuery: IBranchesQuery, onDataResolve?: () => void) {
        // Services and clients we'll need
        const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const gitRestClient = this.pageContext.getRestClient<IGitRestClient>(GitClientName);
        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const repository = vcViewModelDataProviderService.getRepository()!;

        const tableProvider = new BranchTableProvider(
            [],
            undefined,
            undefined,
            [],
            reposRoutingService,
            gitRestClient,
            repository,
            branchesQuery.repositoryName,
            branchesQuery.projectName,
            this.isTargetBranchUpdates2Enabled,
            3
        );

        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const fetchBranchProviderDataScenario = performanceService.startScenario("branches-service-fetch-branch-table-provider-data", false);

        this.getBranchesData(branchesQuery.projectName, branchesQuery.repositoryName, branchesQuery.branchTypeFilter, branchesQuery.filterText).then(
            branchesProviderData => {
                if (!branchesProviderData) {
                    return;
                }

                const branchDataResult = this.parseBranchDataForQuery(branchesQuery, branchesProviderData);

                const enhancedBranches = this.enhanceRefs(
                    branchDataResult.branches || [],
                    branchesProviderData,
                    branchesQuery.repositoryName,
                    branchesQuery.projectName,
                    reposRoutingService,
                    gitRestClient,
                    repository
                );

                const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

                const fetchDataHandler: (page: number) => Promise<BranchTableProviderFetchResult> = (page: number) => {
                    const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
                    const fetchMoreScenario = performanceService.startScenario("branches-service-fetch-more-async", false);

                    // TODO: Abstract out?
                    return contributionService
                        .getDataAsync(
                            this.getContributionIdForFilter(branchesQuery.branchTypeFilter),
                            this.getContributionAsyncParams(page, branchesQuery.repositoryName)
                        )
                        .then((results: IBranchesProviderData) => {
                            // TODO: Figure out how to determine what to look at based on query?
                            const branches = results["Git.Branches.Stale"] || [];

                            const parsedResults: ISparseTreeProviderFetchResult<EnhancedGitRef> = {
                                data: this.enhanceRefs(
                                    branches,
                                    results,
                                    branchesQuery.repositoryName,
                                    branchesQuery.projectName,
                                    reposRoutingService,
                                    gitRestClient,
                                    repository
                                ).branches,
                                hasMore: results["Git.Branches.Stale.HasMore"] === undefined ? false : results["Git.Branches.Stale.HasMore"]
                            };

                            performanceService.endScenario("Repos", fetchMoreScenario.scenarioName, false);

                            return parsedResults;
                        });
                };

                tableProvider.initialize(
                    enhancedBranches.branches,
                    enhancedBranches.compareBranch,
                    enhancedBranches.defaultBranch,
                    branchesProviderData["Git.Branches.Policies"] || [],
                    fetchDataHandler,
                    branchesProviderData["Git.Branches.Stale.HasMore"],
                    enhancedBranches.isUserSetCompareBranch
                );

                onDataResolve && onDataResolve();
                performanceService.endScenario("Repos", fetchBranchProviderDataScenario.scenarioName, false);
            }
        );

        return tableProvider;
    }
    public setBranchesFilter(value: string) {
        this.filterString = value;
    }

    public getBranchTreeProvider(branchesQuery: IBranchesQuery, onDataResolve?: () => void) {
        if ($DEBUG && branchesQuery.branchTypeFilter === BranchTypeFilter.Stale) {
            console.warn(
                "Attempted to get a Tree provider for the Stale tab; Stale should use Tables as the data isn't returned in a Tree-ordered format"
            );
        }

        if (branchesQuery.filterText) {
            this.filterString = branchesQuery.filterText;
        }

        // Removes refs/heads/
        const enhancedIdentifierFunction = (enhancedRef: EnhancedGitRef) => refToBranchName(enhancedRef.rootRef.name);

        // Services and clients we'll need
        const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const gitRestClient = this.pageContext.getRestClient<IGitRestClient>(GitClientName);
        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const repository = vcViewModelDataProviderService.getRepository()!;

        const treeProvider = new BranchTreeProvider(
            new SparseTreeProvider([], enhancedIdentifierFunction, BranchSeparator, 0, 3),
            undefined,
            undefined,
            reposRoutingService,
            gitRestClient,
            [],
            [],
            repository,
            branchesQuery.repositoryName,
            branchesQuery.projectName,
            this.isTargetBranchUpdates2Enabled
        );

        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const fetchBranchProviderDataScenario = performanceService.startScenario("branches-service-fetch-branch-table-provider-data", false);

        this.getBranchesData(branchesQuery.projectName, branchesQuery.repositoryName, branchesQuery.branchTypeFilter, branchesQuery.filterText).then(
            branchesProviderData => {
                if (!branchesProviderData) {
                    return;
                }

                const branchDataResult = this.parseBranchDataForQuery(branchesQuery, branchesProviderData);
                const branches = branchDataResult.branches || [];
                const asyncFlag = branchDataResult.fetchAsync;

                const enhanceResult = this.enhanceRefs(
                    branches,
                    branchesProviderData,
                    branchesQuery.repositoryName,
                    branchesQuery.projectName,
                    reposRoutingService,
                    gitRestClient,
                    repository
                );

                if (asyncFlag) {
                    const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

                    const fetchDataHandler = (item: ITreeItemEx<SparseTreeNode<EnhancedGitRef>>, page: number) => {
                        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
                        const fetchMoreScenario = performanceService.startScenario("branches-service-fetch-more-async", false);
                        return contributionService
                            .getDataAsync(
                                this.getContributionIdForFilter(branchesQuery.branchTypeFilter),
                                this.getContributionAsyncParams(
                                    page,
                                    branchesQuery.repositoryName,
                                    item.underlyingItem.data.absoluteIdentifier,
                                    this.filterString
                                )
                            )
                            .then((results: IBranchesProviderData) => {
                                const branches = results["Git.Branches.Folder"] || [];

                                const parsedResults: ISparseTreeProviderFetchResult<EnhancedGitRef> = {
                                    data: this.enhanceRefs(
                                        branches,
                                        results,
                                        branchesQuery.repositoryName,
                                        branchesQuery.projectName,
                                        reposRoutingService,
                                        gitRestClient,
                                        repository
                                    ).branches,
                                    hasMore: results["Git.Branches.Folder.HasMore"] === undefined ? false : results["Git.Branches.Folder.HasMore"]
                                };

                                performanceService.endScenario("Repos", fetchMoreScenario.scenarioName, false);

                                return parsedResults;
                            });
                    };

                    treeProvider.initialize(
                        enhanceResult.compareBranch,
                        enhanceResult.defaultBranch,
                        branchesProviderData["Git.Branches.Favorites"] || [],
                        branchesProviderData["Git.Branches.Policies"] || [],
                        enhanceResult.isUserSetCompareBranch
                    );
                    treeProvider.baseProvider.initialize(enhanceResult.branches, 0, fetchDataHandler);
                } else {
                    treeProvider.initialize(
                        enhanceResult.compareBranch,
                        enhanceResult.defaultBranch,
                        branchesProviderData["Git.Branches.Favorites"] || [],
                        branchesProviderData["Git.Branches.Policies"] || [],
                        enhanceResult.isUserSetCompareBranch
                    );
                    treeProvider.baseProvider.initialize(enhanceResult.branches, 30);
                }

                onDataResolve && onDataResolve();
                performanceService.endScenario("Repos", fetchBranchProviderDataScenario.scenarioName, false);
            }
        );

        return treeProvider;
    }

    public getSelectedTabId(): IReadonlyObservableValue<string> {
        return this.selectedTabId;
    }

    public searchDeletedBranches(repositoryId: string, projectId: string, queryString: string): ObservableArray<DeletedBranch | undefined> {
        const loadingRows = 3;
        const result = new ObservableArray<DeletedBranch | undefined>(new Array(loadingRows).fill(new ObservableValue(undefined)));

        const refName = branchNameToRef(queryString);
        getGitClient(this.pageContext)
            .getPushes(repositoryId, projectId, 0, 1, { refName, includeRefUpdates: true } as GitPushSearchCriteria)
            .then(queryResult => {
                const deletedUpdates: DeletedBranch[] = [];
                for (const push of queryResult) {
                    for (const refUpdate of push.refUpdates) {
                        if (refUpdate && refUpdate.newObjectId === EMPTY_OBJECT_ID && refUpdate.name === refName) {
                            deletedUpdates.push({
                                branchName: refToBranchName(refUpdate.name),
                                pushId: push.pushId,
                                date: push.date,
                                oldObjectId: refUpdate.oldObjectId,
                                pushedBy: push.pushedBy,
                                refName: refUpdate.name
                            });
                        }
                    }
                }
                result.value = deletedUpdates;
            });

        return result;
    }

    public setDefaultBranch = (projectId: string, repositoryId: string, refName: string): Promise<boolean> => {
        const url = this.getServiceUrl("SetGitDefaultRef", projectId, repositoryId);
        const verificationInput = document.querySelector<HTMLInputElement>("input[name=__RequestVerificationToken]");
        const verificationToken = verificationInput ? verificationInput.value : "";
        const options: RequestInit = {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: `repositoryId=${encodeURIComponent(repositoryId)}&refName=${encodeURIComponent(
                refName
            )}&__RequestVerificationToken=${encodeURIComponent(verificationToken)}`
        };

        return issueRequest(url, options)
            .catch(error => {
                getReposMessagesService(this.pageContext).showMessage({
                    renderMessage: () => error.message,
                    severity: MessageCardSeverity.Error
                });

                return false;
            })
            .then((response: Response) => response && response.ok);
    };

    public setSelectedTabId(selectedTabId: string): void {
        this.selectedTabId.value = selectedTabId;
    }

    private enhanceRefs(
        branches: GitRef[],
        branchesData: IBranchesProviderData,
        repositoryName: string,
        projectName: string,
        reposRoutingService: IReposRoutingService,
        restClient: IGitRestClient,
        repository: GitRepository
    ): EnhanceResult {
        const identifierFunction = (branch: GitRef) => branch.name;
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const enhanceScenario = performanceService.startScenario("branches-service-enhance-branches", false);

        // Build and cache new favorites, or use the most recently retrieved ones if none provided
        const favoritesMap = branchesData["Git.Branches.Favorites"]
            ? mapFromArray(branchesData["Git.Branches.Favorites"], x => x.name)
            : this.mostRecentFavorites;
        this.mostRecentFavorites = favoritesMap;

        const userCreatedMap = mapFromArray(branchesData["Git.Branches.UserCreated"], x => x.name);
        const defaultBranch = branchesData["Git.Branches.Default"];
        const compareBranch = branchesData["Git.Branches.Compare"] || defaultBranch;
        const isUserSetCompare = !!branchesData["Git.Branches.Compare"];
        const metadataMap = mapFromArray(branchesData["Git.Branches.Metadata"], x => x.commitId);
        const pullRequestMap = mapFromArray(branchesData["Git.Branches.PullRequests"], x => x.sourceRefName);

        const statsObservablesByCommitId: { [key: string]: IObservableValue<GitBranchStats | undefined>[] } = {};
        const versionDescriptorsForBranches: GitVersionDescriptor[] = [];

        const enhance: (branch: GitRef) => EnhancedGitRef = (baseBranch: GitRef) => {
            const baseIdentifier = identifierFunction(baseBranch);
            const isCompare = compareBranch ? baseIdentifier === identifierFunction(compareBranch) : false;
            const isDefault = defaultBranch ? baseIdentifier === identifierFunction(defaultBranch) : false;

            // Update external stores for fetching of stats
            const statsObservable = new ObservableValue(undefined);
            if (statsObservablesByCommitId[baseBranch.objectId]) {
                statsObservablesByCommitId[baseBranch.objectId].push(statsObservable);
            } else {
                statsObservablesByCommitId[baseBranch.objectId] = [statsObservable];
            }
            versionDescriptorsForBranches.push({
                version: baseBranch.objectId,
                versionType: GitVersionType.Commit,
                versionOptions: GitVersionOptions.None
            });

            const baseVersion = branchToVersionDescriptor(refToBranchName(baseBranch.name));

            let createPullRequestUrl: string | undefined;
            if (this.isTargetBranchUpdates2Enabled) {
                createPullRequestUrl = compareBranch && !isCompare
                    ? reposRoutingService.getPullRequestCreateUrl(
                        repositoryName,
                        projectName,
                        refToBranchName(baseBranch.name),
                        refToBranchName(compareBranch.name)
                    )
                    : undefined;
            } else {
                createPullRequestUrl = defaultBranch && !isDefault
                    ? reposRoutingService.getPullRequestCreateUrl(
                        repositoryName,
                        projectName,
                        refToBranchName(baseBranch.name),
                        refToBranchName(defaultBranch.name)
                    )
                    : undefined;
            }

            return {
                rootRef: baseBranch,
                refUrl: reposRoutingService.getFilesUrl(repositoryName, projectName, undefined, baseVersion),
                isUserCreated: userCreatedMap[baseIdentifier] ? true : false,
                isCompare,
                isDefault,
                favorite: favoritesMap[baseIdentifier],
                metadata: metadataMap[baseBranch.objectId],
                pullRequest: pullRequestMap[baseBranch.name],
                pullRequestUrl: pullRequestMap[baseBranch.name]
                    ? reposRoutingService.getPullRequestUrl(repositoryName, projectName, pullRequestMap[baseBranch.name].pullRequestId)
                    : undefined,
                createPullRequestUrl: createPullRequestUrl,
                policiesUrl: reposRoutingService.getBranchPolicyUrl(repositoryName, projectName, baseBranch.name),
                securityUrl: reposRoutingService.getBranchSecurityUrl(repository.id, projectName, baseBranch.name),
                compareUrl:
                    compareBranch &&
                    reposRoutingService.getBranchCompareUrl(
                        repositoryName,
                        projectName,
                        branchToVersionDescriptor(refToBranchName(compareBranch.name)),
                        baseVersion
                    ),
                reverseCompareUrl:
                    compareBranch &&
                    reposRoutingService.getBranchCompareUrl(
                        repositoryName,
                        projectName,
                        baseVersion,
                        branchToVersionDescriptor(refToBranchName(compareBranch.name))
                    ),
                commitUrl: reposRoutingService.getCommitUrl(repositoryName, projectName, baseBranch.objectId, branchNameToRef(baseBranch.name)),
                historyUrl: reposRoutingService.getCommitsUrl(repositoryName, projectName, {
                    version: refToBranchName(baseBranch.name),
                    versionType: GitVersionType.Branch,
                    versionOptions: GitVersionOptions.None
                }),
                stats: statsObservable
            };
        };

        const enhancedCompareBranch = compareBranch && enhance(compareBranch);
        const enhancedDefaultBranch = defaultBranch && enhance(defaultBranch);

        const enhancedRefs = branches.map(enhance);

        // Start fetching stats
        if (repository && compareBranch) {
            restClient
                .getBranchStatsBatch(
                    {
                        baseCommit: { version: compareBranch.objectId, versionType: GitVersionType.Commit, versionOptions: GitVersionOptions.None },
                        targetCommits: versionDescriptorsForBranches
                    },
                    repository.id,
                    projectName
                )
                .then(result => {
                    result.forEach(branchStat => {
                        if (statsObservablesByCommitId[branchStat.name]) {
                            statsObservablesByCommitId[branchStat.name].forEach(observable => (observable.value = branchStat));
                        }
                    });
                });
        }

        performanceService.endScenario("Repos", enhanceScenario.scenarioName, false);

        return {
            branches: enhancedRefs,
            compareBranch: enhancedCompareBranch,
            defaultBranch: enhancedDefaultBranch,
            isUserSetCompareBranch: isUserSetCompare
        };
    }

    private getBranchesData(
        projectName: string,
        repositoryName: string,
        branchTypeFilter: BranchTypeFilter,
        filterString?: string
    ): Promise<IBranchesProviderData | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const contributionId = this.getContributionIdForFilter(branchTypeFilter);

        const branchProperties: DataProviderParams = {
            projectName: projectName,
            repositoryName: repositoryName,
            filterString
        };

        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const resolveDataProviderScenario = performanceService.startScenario("branches-service-resolve-data-provider", false, undefined, {
            providerId: contributionId
        });

        return contributionService
            .getDataAsync<IBranchesProviderData>(
                contributionId,
                branchTypeFilter !== BranchTypeFilter.Mine ? branchProperties : undefined,
                filterString !== undefined
            )
            .then(result => {
                performanceService.endScenario("Repos", resolveDataProviderScenario.scenarioName, false);
                return result;
            });
    }

    private getContributionIdForFilter(branchTypeFilter: BranchTypeFilter) {
        switch (branchTypeFilter) {
            case BranchTypeFilter.All:
                return AllBranchesDataProviderContributionId;

            case BranchTypeFilter.Stale:
                return StaleBranchesDataProviderContributionId;
        }

        return MyBranchesDataProviderContributionId;
    }

    private getContributionAsyncParams(page: number, repositoryName: string, folderName?: string, filterString?: string) {
        return {
            HasMoreFolder: folderName,
            repositoryName: repositoryName,
            folderPage: folderName ? page : undefined,
            stalePage: folderName ? undefined : page,
            filterString
        };
    }

    private getServiceUrl = (route: string, projectId: string, repositoryId?: string): string => {
        const hostPath = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostPath || "";
        const repositoryUrlPart = repositoryId ? `&repositoryId=${repositoryId}` : "";
        const url = combineUrlPaths(encodeURI(hostPath), `${projectId}/_api/_versioncontrol/${route}?__v=5${repositoryUrlPart}`);
        return url;
    };

    private parseBranchDataForQuery(branchesQuery: IBranchesQuery, branchesData: IBranchesProviderData): BranchDataResult {
        if (branchesQuery.filterText) {
            return { branches: branchesData["Git.Branches.Filter"], fetchAsync: branchesData["Git.Branches.Filter.FetchData"] };
        } else {
            switch (branchesQuery.branchTypeFilter) {
                case BranchTypeFilter.Mine:
                    return { branches: branchesData["Git.Branches.Mine"] };

                case BranchTypeFilter.All:
                    return { branches: branchesData["Git.Branches.All"], fetchAsync: branchesData["Git.Branches.All.FetchData"] };

                case BranchTypeFilter.Stale:
                    return { branches: branchesData["Git.Branches.Stale"], fetchAsync: branchesData["Git.Branches.Stale.HasMore"] };

                default:
                    return {};
            }
        }
    }
}

const serviceName = "IBranchesService";
export function getBranchesService(context: IVssPageContext) {
    return context.getService<IBranchesService>(serviceName);
}

Services.add(serviceName, { serviceFactory: GitBranchesService });
